import * as THREE from 'three';
import { clamp, smoothstep } from './rng.js';

// ---------------------------------------------------------------------------
// One clock, one set of uniforms, shared by every material in the scene.
// Sky, water, terrain and windmills all read the same sun — so they can never
// disagree about what time it is.
// ---------------------------------------------------------------------------

const c = (hex) => new THREE.Color(hex).convertSRGBToLinear();

// Kept in sync with MAX_LIGHTS in core/materials.js.
export const MAX_LIGHTS = 24;

// Keyframes around the Dutch day. Interpolated by hour.
const STOPS = [
    // Night is moonlit, not blacked out — the polder still has to be readable.
    { h: 0.0, sun: 0x26314f, sky: 0x101a33, hor: 0x1e2a49, amb: 0x3b4f7d, fog: 0x1e2a49 },
    { h: 5.0, sun: 0x39416a, sky: 0x162340, hor: 0x333757, amb: 0x4a5580, fog: 0x333757 },
    { h: 6.6, sun: 0xff9d5c, sky: 0x2e4a7a, hor: 0xe08a5e, amb: 0x4d5a7d, fog: 0xd08b66 },
    { h: 8.0, sun: 0xffd9a8, sky: 0x4c86c4, hor: 0xbcd3e8, amb: 0x7d93b5, fog: 0xc3d7ea },
    { h: 12.0, sun: 0xfff4e0, sky: 0x3f86d4, hor: 0xd3e6f5, amb: 0x93aecb, fog: 0xd3e6f5 },
    { h: 16.0, sun: 0xffe9c0, sky: 0x4785cc, hor: 0xcfe0ee, amb: 0x8ba5c2, fog: 0xcfe0ee },
    { h: 18.6, sun: 0xff8a4a, sky: 0x2f4f86, hor: 0xef9a63, amb: 0x5c5f83, fog: 0xe2915f },
    { h: 20.2, sun: 0x8a5a86, sky: 0x1b2750, hor: 0x6b4a70, amb: 0x44496e, fog: 0x5d4566 },
    { h: 22.0, sun: 0x26314f, sky: 0x101a33, hor: 0x1e2a49, amb: 0x3b4f7d, fog: 0x1e2a49 },
    { h: 24.0, sun: 0x26314f, sky: 0x101a33, hor: 0x1e2a49, amb: 0x3b4f7d, fog: 0x1e2a49 },
];

export class Environment {
    constructor({ dayLength = 150, startHour = 10 } = {}) {
        this.dayLength = dayLength;
        this.hour = startHour;
        this.paused = false;
        this.elapsed = 0;

        // Shared uniform objects — handed by reference to every material.
        this.uniforms = {
            uTime: { value: 0 },
            uSunDir: { value: new THREE.Vector3(0.4, 0.8, 0.35) },
            uSunColor: { value: new THREE.Color(1, 1, 1) },
            uSkyColor: { value: new THREE.Color(0.4, 0.6, 0.9) },
            uHorizonColor: { value: new THREE.Color(0.8, 0.88, 0.95) },
            uAmbientColor: { value: new THREE.Color(0.5, 0.6, 0.75) },
            uFogColor: { value: new THREE.Color(0.8, 0.88, 0.95) },
            uFogRange: { value: new THREE.Vector2(40, 190) },
            uNight: { value: 0 },
            uSunElevation: { value: 1 },

            // Moonlight: a second key light, opposite the sun and much cooler.
            uMoonDir: { value: new THREE.Vector3(0, 1, 0) },
            uMoonColor: { value: new THREE.Color(0x9fb4e8).convertSRGBToLinear() },

            // Practical lights — windows, lanterns, the mill doors.
            uLightPos: { value: Array.from({ length: MAX_LIGHTS }, () => new THREE.Vector3()) },
            uLightColor: { value: Array.from({ length: MAX_LIGHTS }, () => new THREE.Color()) },
            uLightRange: { value: new Float32Array(MAX_LIGHTS) },
            uLightCount: { value: 0 },
        };

        this.lights = [];
        this.update(0);
    }

    /**
     * Register a warm practical light. They only contribute after dark, which
     * is what makes the night read as a village rather than a silhouette.
     */
    addLight(position, { color = 0xffb35c, intensity = 1, range = 9 } = {}) {
        if (this.lights.length >= MAX_LIGHTS) return false;
        const i = this.lights.length;
        this.uniforms.uLightPos.value[i].copy(position);
        this.uniforms.uLightColor.value[i]
            .set(color)
            .convertSRGBToLinear()
            .multiplyScalar(intensity);
        this.uniforms.uLightRange.value[i] = range;
        this.uniforms.uLightCount.value = i + 1;
        this.lights.push({ position, color, intensity, range });
        return true;
    }

    setHour(h) {
        this.hour = ((h % 24) + 24) % 24;
        this.refresh();
    }

    update(dt) {
        if (!this.paused) {
            this.hour = (this.hour + (dt / this.dayLength) * 24) % 24;
        }
        this.elapsed += dt;
        this.uniforms.uTime.value = this.elapsed;
        this.refresh();
    }

    refresh() {
        const h = this.hour;
        const u = this.uniforms;

        // --- sun position -------------------------------------------------
        // Sunrise at 6, zenith at 12, sunset at 18. Tilted arc so the light
        // rakes across the polder instead of coming straight down.
        const dayAngle = ((h - 6) / 12) * Math.PI;
        const elevation = Math.sin(dayAngle);
        const azimuth = Math.cos(dayAngle);
        u.uSunDir.value.set(azimuth * 0.85, Math.max(elevation, -1) * 0.95 + 0.02, 0.42).normalize();
        u.uSunElevation.value = elevation;

        // The moon rides the opposite arc, tilted so it is never a perfect
        // mirror of the sun — otherwise the two keys cancel at the horizon.
        u.uMoonDir.value
            .set(-azimuth * 0.8, -elevation * 0.85 + 0.22, -0.36)
            .normalize();

        // --- palette ------------------------------------------------------
        let a = STOPS[0];
        let b = STOPS[STOPS.length - 1];
        for (let i = 0; i < STOPS.length - 1; i++) {
            if (h >= STOPS[i].h && h <= STOPS[i + 1].h) {
                a = STOPS[i];
                b = STOPS[i + 1];
                break;
            }
        }
        const t = b.h === a.h ? 0 : smoothstep(a.h, b.h, h);

        u.uSunColor.value.copy(c(a.sun)).lerp(c(b.sun), t);
        u.uSkyColor.value.copy(c(a.sky)).lerp(c(b.sky), t);
        u.uHorizonColor.value.copy(c(a.hor)).lerp(c(b.hor), t);
        u.uAmbientColor.value.copy(c(a.amb)).lerp(c(b.amb), t);
        u.uFogColor.value.copy(c(a.fog)).lerp(c(b.fog), t);

        // Night factor drives window glow and star opacity.
        u.uNight.value = clamp(1 - smoothstep(-0.18, 0.12, elevation), 0, 1);
    }

    /** Human-readable clock for the HUD. */
    clockLabel() {
        const hh = Math.floor(this.hour);
        const mm = Math.floor((this.hour - hh) * 60);
        return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    }
}
